import React, { useEffect } from "react";
import { useMasterData } from "../context/useMasterData";
import TruncatedCell from "../../../../globalComponents/ui/TruncatedCell";
import HoverExpandInput from "../../../../globalComponents/ui/HoverExpandInput";
import { classifyQuestionShape, columnsForRow } from "../services/answerShape";

const injectTableStyles = () => {
    if (document.getElementById("tdf-styles")) return;
    const s = document.createElement("style");
    s.id = "tdf-styles";
    s.textContent = `
        .tdf-wrap { width:100%; overflow-x:auto; }
        .tdf-table { width:max-content; min-width:100%; border-collapse:collapse; }
        /* uniform-column variants: columns share the available width, so cells
           only ellipsize when space actually runs out */
        .tdf-table-fixed { table-layout:fixed; width:100%; }

        /* ── column headers: indigo band, mono small-caps ── */
        .tdf-th {
            padding:10px 14px; text-align:left;
            background:#e9efff; border-bottom:2px solid #c3d1fb;
            font-size:10px; font-weight:700; color:#3346c2;
            text-transform:uppercase; letter-spacing:0.08em;
            font-family:'JetBrains Mono','Fira Mono',monospace;
            white-space:nowrap;
        }

        /* ── data cells: white, blue-tinted zebra + hover ── */
        .tdf-tr:nth-child(even) .tdf-td,
        .tdf-tr:nth-child(even) .tdf-td-rm { background:#fafbff; }
        .tdf-tr:hover .tdf-td,
        .tdf-tr:hover .tdf-td-rm { background:#edf3ff !important; }

        .tdf-td {
            padding:10px 14px;
            border-bottom:1px solid #eef1f8;
            vertical-align:middle;
            background:#fff;
            font-size:13px; color:#1e293b; line-height:1.5;
        }

        /* ── category / row-label cells: tinted, sentence case ── */
        .tdf-td-cat {
            padding:10px 14px;
            border-bottom:1px solid #e3e9fa;
            vertical-align:middle;
            background:#f5f7ff;
            font-size:12.5px; font-weight:600; color:#1e2a5e;
            white-space:nowrap;
            border-right:2px solid #dbe4fd;
            position:sticky; left:0; z-index:1;
        }
        .tdf-tr:hover .tdf-td-cat { background:#eef3ff; }

        /* ── per-row section band (fixed rows / dynamic columns) ── */
        .tdf-td-row-label {
            padding:7px 14px; font-size:10px; font-weight:700;
            color:#3346c2; background:#eef3ff;
            border-bottom:1px solid #dbe4fd;
            border-left:3px solid #6366f1;
            letter-spacing:0.08em; text-transform:uppercase;
            font-family:'JetBrains Mono','Fira Mono',monospace;
        }

        /* ── in-cell column label (rows with their own columns) ── */
        .tdf-cell-label {
            font-size:10px; font-weight:700; color:#3346c2;
            margin-bottom:5px; text-transform:uppercase; letter-spacing:0.08em;
            font-family:'JetBrains Mono','Fira Mono',monospace;
        }

        .tdf-td-rm {
            padding:8px; text-align:center; width:36px;
            border-bottom:1px solid #eef1f8; background:#fff;
        }

        .tdf-empty { color:#c7d0de; font-size:13px; }
        .tdf-unit { font-size:11.5px; color:#5b6b8c; margin-left:5px; }

        .tdf-input {
            width:100%; padding:6px 10px; border-radius:6px;
            border:1.5px solid #e2e8f0; font-size:13px;
            font-family:'DM Sans','Segoe UI',sans-serif;
            color:#0f172a; transition:border-color .15s, box-shadow .15s;
            outline:none; box-sizing:border-box; background:#fff;
        }
        .tdf-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.12); }
        .tdf-input:disabled { background:#f8fafc; color:#94a3b8; cursor:default; border-color:#e2e8f0; }

        .tdf-toolbar {
            display:flex; gap:8px; padding:10px 14px; flex-wrap:wrap;
            border-bottom:1.5px solid #e3e9fa; background:#f8faff; align-items:center;
        }
        .tdf-toolbar-btn {
            display:inline-flex; align-items:center; gap:4px;
            padding:5px 12px; border-radius:7px; font-size:12px; font-weight:600;
            cursor:pointer; border:1.5px solid #e2e8f0;
            background:#fff; color:#475569;
            font-family:'DM Sans','Segoe UI',sans-serif;
            transition:all .15s; white-space:nowrap;
        }
        .tdf-toolbar-btn:hover { background:#eff6ff; border-color:#bfdbfe; color:#2563eb; }

        .tdf-groups-dropdown { position:relative; }
        .tdf-groups-btn {
            display:inline-flex; align-items:center; gap:6px;
            padding:5px 12px; border-radius:7px; font-size:12px; font-weight:600;
            cursor:pointer; border:1.5px solid #c7d2fe;
            background:#eef2ff; color:#4338ca;
            font-family:'DM Sans','Segoe UI',sans-serif;
            transition:all .15s; white-space:nowrap;
        }
        .tdf-groups-btn:hover { background:#e0e7ff; border-color:#a5b4fc; }
        .tdf-groups-panel {
            position:absolute; top:calc(100% + 6px); left:0;
            background:#fff; border:1.5px solid #e2e8f0;
            border-radius:10px; box-shadow:0 8px 24px rgba(15,23,42,.12);
            padding:8px; z-index:200; min-width:230px;
        }
        .tdf-group-row {
            display:flex; align-items:center; gap:6px;
            padding:4px 6px; border-radius:6px; transition:background .12s;
        }
        .tdf-group-row:hover { background:#f8fafc; }
        .tdf-group-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
        .tdf-group-input {
            flex:1; padding:4px 8px; border-radius:5px;
            border:1px solid transparent; font-size:12.5px;
            font-family:'DM Sans','Segoe UI',sans-serif;
            color:#1e293b; outline:none; box-sizing:border-box; background:transparent;
        }
        .tdf-group-input:hover { border-color:#e2e8f0; }
        .tdf-group-input:focus { border-color:#3b82f6; background:#fff; }
        .tdf-groups-divider { height:1px; background:#f1f5f9; margin:6px 2px; }
        .tdf-groups-add {
            display:flex; align-items:center; gap:6px;
            padding:5px 8px; border-radius:6px; font-size:12.5px;
            font-weight:600; color:#3b82f6; cursor:pointer;
            width:100%; border:none; background:transparent;
            font-family:'DM Sans','Segoe UI',sans-serif;
            transition:background .12s;
        }
        .tdf-groups-add:hover { background:#eff6ff; }

        .tdf-group-tabs {
            display:flex; border-bottom:2px solid #dbe4fd;
            background:#f8faff; overflow-x:auto; flex-shrink:0;
        }
        .tdf-group-tab {
            display:inline-flex; align-items:center; gap:6px;
            padding:9px 18px; font-size:12.5px; font-weight:500;
            color:#5b6b8c; cursor:pointer; border:none; background:transparent;
            white-space:nowrap; border-bottom:2px solid transparent;
            margin-bottom:-2px; font-family:'DM Sans','Segoe UI',sans-serif;
            transition:color .15s, border-color .15s, background .15s;
        }
        .tdf-group-tab:hover { color:#1e2a5e; background:#eef3ff; }
        .tdf-group-tab-active { color:#4f46e5 !important; font-weight:700; border-bottom-color:#4f46e5 !important; background:#fff !important; }
        .tdf-group-tab-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }

        .tdf-add-row-wrap { padding:10px 14px; }
        .tdf-rm-btn {
            background:none; border:none; cursor:pointer;
            font-size:15px; color:#cbd5e1; padding:2px 5px; border-radius:5px;
            transition:color .15s, background .15s; line-height:1; flex-shrink:0;
        }
        .tdf-rm-btn:hover:not(:disabled) { color:#dc2626; background:#fee2e2; }
        .tdf-rm-btn:disabled { cursor:not-allowed; opacity:0.4; }
    `;
    document.head.appendChild(s);
};

/* clamps to the cell width by default (fixed-layout tables); pass a px cap
   for the auto-layout per-row-columns variant where "100%" can't resolve */
const displayVal = (v, maxWidth = "100%") =>
    (v !== null && v !== undefined && v !== "")
        ? <TruncatedCell text={String(v)} maxWidth={maxWidth} />
        : <span className="tdf-empty">—</span>;

/* headers / category labels: same clamp + hover popup, blank fallback */
const labelVal = (t, maxWidth = "100%") => (t ? <TruncatedCell text={String(t)} maxWidth={maxWidth} /> : " ");

const GROUP_COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4"];

const GroupsDropdown = ({ groups, onRename, onRemove, onAdd }) => {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);
    useEffect(() => {
        if (!open) return;
        const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [open]);
    return (
        <div className="tdf-groups-dropdown" ref={ref}>
            <button type="button" className="tdf-groups-btn" onClick={() => setOpen(v => !v)}>
                <span>Groups ({groups.length})</span>
                <span style={{ fontSize: 9, opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
            </button>
            {open && (
                <div className="tdf-groups-panel">
                    {groups.map((gName, gIdx) => (
                        <div key={gIdx} className="tdf-group-row">
                            <div className="tdf-group-dot" style={{ background: GROUP_COLORS[gIdx % GROUP_COLORS.length] }} />
                            <input
                                className="tdf-group-input"
                                value={gName}
                                onChange={e => onRename(gIdx, e.target.value)}
                                placeholder={`Group ${gIdx + 1}`}
                            />
                            <button type="button" onClick={() => onRemove(gIdx)} className="tdf-rm-btn" title="Remove group">×</button>
                        </div>
                    ))}
                    <div className="tdf-groups-divider" />
                    <button type="button" className="tdf-groups-add" onClick={onAdd}>+ Add Group</button>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Fully-Dynamic sub-component
// No groups (2D): gridData[0] = string headers, gridData[1+] = {value,unit_id} cells
// With groups (3D): gridData[0][gIdx] = string[] headers, gridData[r][gIdx][cIdx] = {value,unit_id}
// ─────────────────────────────────────────────────────────────────────────────
const FullyDynamicForm = ({ gridData, setGridData, readOnly, predefinedRows, predefinedColumns, columnGroups, setColumnGroups, setRowNames = () => {} }) => {
    useEffect(() => { injectTableStyles(); }, []);

    const [activeGroupIdx, setActiveGroupIdx] = React.useState(0);

    // gridData dimensionality is the source of truth: 3D means groups, 2D means no groups.
    // columnGroups may be set even if answer was stored 2D (legacy/mismatch), so guard with data check.
    const hasGroups = Array.isArray(columnGroups) && columnGroups.length > 0 && Array.isArray(gridData?.[0]?.[0]);
    const hasPredefinedRows = Array.isArray(predefinedRows) && predefinedRows.length > 0;
    const hasPredefinedColumns = !hasGroups && Array.isArray(predefinedColumns) && predefinedColumns.length > 0;
    const numCols = hasGroups ? 0 : (gridData[0] || []).length;
    // max cells any single data row has (for per-row varying column support)
    const maxDataCols = hasGroups ? 0 : Math.max(0, ...gridData.slice(1).map(r => (r || []).length));
    const displayCols = hasGroups ? 0 : Math.max(numCols, maxDataCols, hasPredefinedColumns ? predefinedColumns.length : 0);

    // Clamp activeGroupIdx when groups are removed
    const safeGroupIdx = hasGroups ? Math.min(activeGroupIdx, columnGroups.length - 1) : 0;

    // Pre-populate header row with predefined column labels on first render (flat mode only).
    useEffect(() => {
        if (!hasPredefinedColumns) return;
        setGridData(prev => {
            const currentHeaders = prev[0] || [];
            if (currentHeaders.length >= predefinedColumns.length) return prev;
            const newHeaders = predefinedColumns.map((col, idx) => currentHeaders[idx] ?? (col.option || ""));
            const extraHeaders = currentHeaders.slice(predefinedColumns.length);
            const next = [...prev];
            next[0] = [...newHeaders, ...extraHeaders];
            return next;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleHeaderChange = (gIdx, cIdx, value) => {
        setGridData(prev => {
            if (hasGroups) {
                const next = prev.map(r => (r || []).map(g => [...(g || [])]));
                if (!next[0]) next[0] = [];
                if (!next[0][gIdx]) next[0][gIdx] = [];
                next[0][gIdx][cIdx] = value;
                return next;
            } else {
                const next = prev.map(r => [...(r || [])]);
                if (!next[0]) next[0] = [];
                next[0][cIdx] = value;
                return next;
            }
        });
    };

    // gIdx is colIndex in flat mode; cIdx is within-group colIndex in grouped mode
    const handleCellChange = (rIndex, gIdx, cIdx, patch) => {
        setGridData(prev => {
            if (hasGroups) {
                const next = prev.map(r => (r || []).map(g => [...(g || [])]));
                if (!next[rIndex]) next[rIndex] = [];
                if (!next[rIndex][gIdx]) next[rIndex][gIdx] = [];
                next[rIndex][gIdx][cIdx] = { ...(next[rIndex][gIdx][cIdx] || {}), ...patch };
                return next;
            } else {
                const next = prev.map(r => [...(r || [])]);
                if (!next[rIndex]) next[rIndex] = [];
                next[rIndex][gIdx] = { ...(next[rIndex][gIdx] || {}), ...patch };
                return next;
            }
        });
    };

    const addColumn = (gIdx) => {
        setGridData(prev => {
            if (hasGroups) {
                return prev.map((r, rIdx) => {
                    const next = (r || []).map(g => [...(g || [])]);
                    if (!next[gIdx]) next[gIdx] = [];
                    next[gIdx].push(rIdx === 0 ? "" : { value: "", unit_id: null });
                    return next;
                });
            } else {
                const next = prev.map((r, rIdx) => [...(r || []), rIdx === 0 ? "" : { value: "", unit_id: null }]);
                if (next.length === 0) next.push([""]);
                return next;
            }
        });
    };

    const removeColumn = (gIdx, cIdx) => {
        if (hasGroups) {
            if ((gridData[0]?.[gIdx] || []).length <= 1) return;
            setGridData(prev => prev.map(r => {
                const next = (r || []).map(g => [...(g || [])]);
                next[gIdx] = (next[gIdx] || []).filter((_, i) => i !== cIdx);
                return next;
            }));
        } else {
            const minCols = hasPredefinedColumns ? predefinedColumns.length : 1;
            if (numCols <= minCols) return;
            if (hasPredefinedColumns && cIdx < predefinedColumns.length) return;
            setGridData(prev => prev.map(r => (r || []).filter((_, i) => i !== cIdx)));
        }
    };

    const addColumnToRow = (rIndex) => {
        setGridData(prev => prev.map((r, i) => {
            if (i !== rIndex) return hasGroups ? (r || []).map(g => [...(g || [])]) : [...(r || [])];
            if (hasGroups) {
                // add one cell to every group so all groups stay identical per row
                return (r || []).map(g => [...(g || []), { value: "", unit_id: null }]);
            }
            return [...(r || []), { value: "", unit_id: null }];
        }));
    };

    const removeColumnFromRow = (rIndex, cIdx) => {
        setGridData(prev => prev.map((r, i) => {
            if (i !== rIndex) return hasGroups ? (r || []).map(g => [...(g || [])]) : [...(r || [])];
            if (hasGroups) {
                // remove the same column index from every group
                return (r || []).map(g => (g || []).filter((_, ci) => ci !== cIdx));
            }
            return (r || []).filter((_, ci) => ci !== cIdx);
        }));
    };

    const addRow = () => {
        setRowNames(prev => [...prev, ""]);
        setGridData(prev => {
            if (hasGroups) {
                const newRow = (gridData[0] || []).map(grpHeaders =>
                    (grpHeaders || []).map(() => ({ value: "", unit_id: null }))
                );
                return [...prev, newRow];
            } else {
                const cols = (prev[0] || []).length || 1;
                return [...prev, new Array(cols).fill(null).map(() => ({ value: "", unit_id: null }))];
            }
        });
    };

    const removeRow = (rIndex) => {
        if (rIndex === 0 || gridData.length <= 2) return;
        setGridData(prev => prev.filter((_, i) => i !== rIndex));
        setRowNames(prev => prev.filter((_, ri) => ri !== rIndex - 1));
    };

    const addGroup = () => {
        const name = `Group ${(columnGroups?.length || 0) + 1}`;
        if (!hasGroups) {
            // 2D → 3D: wrap each row in a single-group array
            setGridData(prev => prev.map(row => [(row || []).map(c => (typeof c === "string" ? c : { ...c }))]));
            setColumnGroups([name]);
        } else {
            // Append new group with one empty column to each row
            setGridData(prev => prev.map((row, rIdx) => [
                ...(row || []),
                [rIdx === 0 ? "" : { value: "", unit_id: null }],
            ]));
            setColumnGroups(prev => [...(prev || []), name]);
        }
    };

    const removeGroup = (gIdx) => {
        const newGroups = (columnGroups || []).filter((_, i) => i !== gIdx);
        if (newGroups.length === 0) {
            // 3D → 2D: preserve this group's data as the flat row
            setGridData(prev => prev.map(row => (row || [])[gIdx] || []));
            setColumnGroups([]);
        } else {
            setGridData(prev => prev.map(row => (row || []).filter((_, i) => i !== gIdx)));
            setColumnGroups(newGroups);
        }
    };

    const renameGroup = (gIdx, name) => {
        setColumnGroups(prev => (prev || []).map((g, i) => i === gIdx ? name : g));
    };

    // Derived values for the currently active group
    const activeHeaderCols = hasGroups
        ? (gridData[0]?.[safeGroupIdx] || [""]).map(h => typeof h === "string" ? h : "")
        : [];

    return (
        <div className="tdf-wrap">
            {!readOnly && (
                <div className="tdf-toolbar">
                    {hasGroups ? (
                        <>
                            <GroupsDropdown
                                groups={columnGroups}
                                onRename={renameGroup}
                                onRemove={(gIdx) => {
                                    if (safeGroupIdx >= columnGroups.length - 1) setActiveGroupIdx(Math.max(0, columnGroups.length - 2));
                                    removeGroup(gIdx);
                                }}
                                onAdd={() => { addGroup(); setActiveGroupIdx(columnGroups.length); }}
                            />
                            <button onClick={addRow} className="tdf-toolbar-btn">+ Add Row</button>
                        </>
                    ) : (
                        <>
                            <button onClick={addRow} className="tdf-toolbar-btn">+ Add Row</button>
                            <button onClick={addGroup} className="tdf-toolbar-btn">+ Add Column Groups</button>
                        </>
                    )}
                </div>
            )}

            {/* Group tab selector */}
            {hasGroups && (
                <div className="tdf-group-tabs">
                    {columnGroups.map((gName, gIdx) => (
                        <button
                            key={gIdx}
                            type="button"
                            className={`tdf-group-tab${gIdx === safeGroupIdx ? " tdf-group-tab-active" : ""}`}
                            onClick={() => setActiveGroupIdx(gIdx)}
                        >
                            <span className="tdf-group-tab-dot" style={{ background: GROUP_COLORS[gIdx % GROUP_COLORS.length] }} />
                            {gName || `Group ${gIdx + 1}`}
                        </button>
                    ))}
                </div>
            )}

            <table className="tdf-table tdf-table-fixed">
                <thead>
                    {hasGroups ? (
                        <tr>
                            <th className="tdf-th" style={{ width: 36 }} />
                            {activeHeaderCols.map((header, cIdx) => (
                                <th key={cIdx} className="tdf-th" style={{ minWidth: 160, width: 180 }}>
                                    {readOnly ? labelVal(header) : (
                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <input
                                                type="text"
                                                value={header || ""}
                                                onChange={e => handleHeaderChange(safeGroupIdx, cIdx, e.target.value)}
                                                placeholder={`Col ${cIdx + 1}`}
                                                className="tdf-input"
                                                style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}
                                            />
                                            {activeHeaderCols.length > 1 && (
                                                <button onClick={() => removeColumn(safeGroupIdx, cIdx)} className="tdf-rm-btn" title="Remove column">×</button>
                                            )}
                                        </div>
                                    )}
                                </th>
                            ))}
                            {!readOnly && (
                                <th className="tdf-th" style={{ width: 40 }}>
                                    <button onClick={() => addColumn(safeGroupIdx)} className="tdf-rm-btn" title="Add column" style={{ color: "#3b82f6", fontSize: 18 }}>+</button>
                                </th>
                            )}
                        </tr>
                    ) : (
                        <tr>
                            <th className="tdf-th" style={{ width: 36 }} />
                            {Array.from({ length: displayCols }, (_, cIndex) => {
                                const isPredefinedCol = hasPredefinedColumns && cIndex < predefinedColumns.length;
                                const minCols = hasPredefinedColumns ? predefinedColumns.length : 1;
                                const rawHeader = (gridData[0] || [])[cIndex];
                                const safeHeader = typeof rawHeader === "string" ? rawHeader : "";
                                const headerLabel = isPredefinedCol ? (predefinedColumns[cIndex].option || "") : safeHeader;
                                return (
                                    <th key={cIndex} className="tdf-th" style={{ minWidth: 160, width: 180 }}>
                                        {readOnly || isPredefinedCol ? labelVal(headerLabel) : (
                                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                                <input
                                                    type="text"
                                                    value={safeHeader}
                                                    onChange={e => handleHeaderChange(null, cIndex, e.target.value)}
                                                    placeholder={`Column ${cIndex + 1}`}
                                                    className="tdf-input"
                                                    style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}
                                                />
                                                {numCols > minCols && cIndex < numCols && (
                                                    <button onClick={() => removeColumn(null, cIndex)} className="tdf-rm-btn" title="Remove column">×</button>
                                                )}
                                            </div>
                                        )}
                                    </th>
                                );
                            })}
                            {!readOnly && <th className="tdf-th" style={{ width: 50 }} />}
                        </tr>
                    )}
                </thead>
                <tbody>
                    {gridData.slice(1).map((rawRow, i) => {
                        const rowVals = rawRow ?? []; // stored answers can contain null rows
                        const rIndex = i + 1;
                        const groupCells = hasGroups ? (rowVals[safeGroupIdx] || []) : [];
                        const activeCellCount = hasGroups ? Math.max(activeHeaderCols.length, groupCells.length) : 0;
                        return (
                            <tr key={rIndex} className="tdf-tr">
                                <td className="tdf-td-rm">
                                    {!readOnly && (
                                        <button
                                            onClick={() => removeRow(rIndex)}
                                            disabled={hasPredefinedRows ? gridData.length <= predefinedRows.length + 1 : gridData.length <= 2}
                                            className="tdf-rm-btn"
                                            title="Remove row"
                                        >×</button>
                                    )}
                                </td>
                                {hasGroups ? (
                                    <>
                                        {Array.from({ length: activeCellCount }, (_, cIdx) => {
                                            const cellObj = groupCells[cIdx] || {};
                                            const isExtra = cIdx >= activeHeaderCols.length;
                                            return (
                                                <td key={cIdx} className="tdf-td" style={{ minWidth: 160 }}>
                                                    {readOnly ? displayVal(cellObj.value) : (
                                                        <div style={{ display: "flex", gap: 4 }}>
                                                            <HoverExpandInput
                                                                value={cellObj.value ?? ""}
                                                                onChange={(v) => handleCellChange(rIndex, safeGroupIdx, cIdx, { value: v })}
                                                                placeholder="Enter value..."
                                                            />
                                                            {isExtra && (
                                                                <button onClick={() => removeColumnFromRow(rIndex, cIdx)} className="tdf-rm-btn" title="Remove cell">×</button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        {!readOnly && (
                                            <td className="tdf-td" style={{ width: 40 }}>
                                                <button onClick={() => addColumnToRow(rIndex)} className="tdf-toolbar-btn" style={{ padding: "2px 6px", fontSize: 11 }}>+Col</button>
                                            </td>
                                        )}
                                    </>
                                ) : (
                                    Array.from({ length: Math.max(displayCols, (rowVals || []).length) }, (_, cIndex) => (
                                        <td key={cIndex} className="tdf-td" style={{ minWidth: 160 }}>
                                            {readOnly ? displayVal(rowVals[cIndex]?.value) : (
                                                <div style={{ display: "flex", gap: 4 }}>
                                                    <HoverExpandInput
                                                        value={rowVals[cIndex]?.value ?? ""}
                                                        onChange={(v) => handleCellChange(rIndex, cIndex, 0, { value: v })}
                                                        placeholder="Enter value..."
                                                    />
                                                    {(rowVals || []).length > numCols && cIndex >= numCols && cIndex >= (hasPredefinedColumns ? predefinedColumns.length : 0) && (
                                                        <button onClick={() => removeColumnFromRow(rIndex, cIndex)} className="tdf-rm-btn" title="Remove cell">×</button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    ))
                                )}
                                {!readOnly && !hasGroups && (
                                    <td className="tdf-td" style={{ width: 50 }}>
                                        <button onClick={() => addColumnToRow(rIndex)} className="tdf-toolbar-btn" style={{ padding: "2px 6px", fontSize: 11 }}>+Col</button>
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                    {!hasGroups && gridData.length <= 1 && !readOnly && (
                        <tr>
                            <td colSpan="100%" className="tdf-td" style={{ textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                                Click <strong>+ Add Row</strong> to start entering data.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// columnGroups comes from the answer API response (answerData.columnGroups), not question.
// gridData dimensionality: 2D = no groups, 3D = groups.
// ─────────────────────────────────────────────────────────────────────────────
const TabularDynamicForm = ({
    question,
    gridData,
    setGridData,
    readOnly = false,
    columnGroups = null,
    setColumnGroups = () => {},
    rowNames = [],
    setRowNames = () => {},
}) => {
    useEffect(() => { injectTableStyles(); }, []);

    const { units } = useMasterData();
    const shape = classifyQuestionShape(question?.details);
    const {
        rowOptions,
        colOptions,
        dynamicRowsDynamicColumns,
        dynamicRowsFixedColumns,
        fixedRowsDynamicColumns,
        fixedRowsFixedColumns,
    } = shape;

    const hasGroups = Array.isArray(columnGroups) && columnGroups.length > 0 && Array.isArray(gridData?.[0]?.[0]);
    const colsPerGroup = hasGroups ? columnGroups.map(() => colOptions) : null;

    const [activeGroupIdx, setActiveGroupIdx] = React.useState(0);
    const safeGroupIdx = hasGroups ? Math.min(activeGroupIdx, columnGroups.length - 1) : 0;

    // category column (question-defined row labels): skip when every option is falsy
    const hasAnyCategoryLabel = rowOptions.some((r) => r?.option);

    const getColumnsForRow = (rIndex) => columnsForRow(shape, rIndex);
    // max column count across all fixed rows (for uniform group column span)
    const maxRowCols = fixedRowsDynamicColumns
        ? Math.max(...rowOptions.map((_, rIdx) => getColumnsForRow(rIdx).length), 1)
        : 0;

    // Group management for non-FullyDynamic forms
    const addGroup = () => {
        const name = `Group ${(columnGroups?.length || 0) + 1}`;
        if (!hasGroups) {
            // 2D → 3D: each row becomes [[...original cells]]
            setGridData(prev => prev.map(row => [row.map(cell => ({ ...cell }))]));
            setColumnGroups([name]);
        } else {
            if (fixedRowsDynamicColumns) {
                // per-row column counts differ, so compute per row
                setGridData(prev => prev.map((row, rIndex) => {
                    const newGroupCells = getColumnsForRow(rIndex).map(() => ({ value: "", unit_id: null }));
                    return [...row, newGroupCells];
                }));
            } else {
                const emptyCells = colOptions.map(() => ({ value: "", unit_id: null }));
                setGridData(prev => prev.map(row => [...row, [...emptyCells]]));
            }
            setColumnGroups(prev => [...prev, name]);
        }
    };

    const removeGroup = (gIdx) => {
        const newGroups = (columnGroups || []).filter((_, i) => i !== gIdx);
        if (newGroups.length === 0) {
            // 3D → 2D: preserve data from this (last) group
            setGridData(prev => prev.map(row => (row[gIdx] || [])));
            setColumnGroups([]);
        } else {
            setGridData(prev => prev.map(row => (row || []).filter((_, i) => i !== gIdx)));
            setColumnGroups(newGroups);
        }
    };

    const renameGroup = (gIdx, name) => {
        setColumnGroups(prev => prev.map((g, i) => i === gIdx ? name : g));
    };

    if (dynamicRowsDynamicColumns) {
        return (
            <FullyDynamicForm
                gridData={gridData}
                setGridData={setGridData}
                readOnly={readOnly}
                predefinedRows={rowOptions[0]?.pre_defined_rows}
                predefinedColumns={colOptions[0]?.pre_defined_columns}
                columnGroups={columnGroups}
                setColumnGroups={setColumnGroups}
                rowNames={rowNames}
                setRowNames={setRowNames}
            />
        );
    }

    const handleInputChange = (rIndex, gIndex, cIndex, patch) => {
        if (hasGroups) {
            setGridData(prev => {
                const next = prev.map(row => row.map(grp => [...grp]));
                if (!next[rIndex]) next[rIndex] = [];
                if (!next[rIndex][gIndex]) next[rIndex][gIndex] = [];
                next[rIndex][gIndex][cIndex] = { ...next[rIndex][gIndex][cIndex], ...patch };
                return next;
            });
        } else {
            setGridData(prev => {
                const next = prev.map(row => [...row]);
                if (!next[rIndex]) next[rIndex] = [];
                next[rIndex][gIndex] = { ...next[rIndex][gIndex], ...patch };
                return next;
            });
        }
    };

    const addRow = () => {
        if (!dynamicRowsFixedColumns) return;
        setRowNames(prev => [...prev, ""]);
        if (hasGroups) {
            const newRow = columnGroups.map((_, gIdx) => {
                const cols = colOptions.length > 0 ? colOptions : getColumnsForRow(0);
                return new Array(cols.length).fill(null).map(() => ({ value: "", unit_id: null }));
            });
            setGridData(prev => [...prev, newRow]);
        } else {
            const columns = getColumnsForRow(0);
            setGridData(prev => [...prev, new Array(columns.length).fill(null).map(() => ({ value: "", unit_id: null }))]);
        }
    };

    const removeRow = (rIndex) => {
        if (!dynamicRowsFixedColumns || gridData.length <= 1) return;
        setGridData(prev => prev.filter((_, i) => i !== rIndex));
        setRowNames(prev => prev.filter((_, i) => i !== rIndex));
    };

    const renderCell = (col, cellObj, rIndex, gIndex, cIndex) => {
        cellObj = cellObj ?? {};
        // auto-layout variant: "100%" can't resolve inside auto table cells, cap in px
        const cellMax = fixedRowsDynamicColumns ? 560 : undefined;
        return (
            <td key={`${gIndex}-${cIndex}`} className="tdf-td" style={{ minWidth: 160 }}>
                {fixedRowsDynamicColumns && (
                    <div className="tdf-cell-label">
                        {labelVal(col.option, 560)}
                    </div>
                )}
                {col.data_type === "dropdown" ? (
                    readOnly ? (
                        displayVal(cellObj.value, cellMax)
                    ) : (
                        <select
                            value={cellObj.value || ""}
                            onChange={e => handleInputChange(rIndex, gIndex, cIndex, { value: e.target.value, unit_id: null })}
                            className="tdf-input"
                        >
                            <option value="" disabled>Select...</option>
                            {(col.dropdown_options || []).map((opt, i) => (
                                <option key={i} value={opt}>{opt}</option>
                            ))}
                        </select>
                    )
                ) : col.data_type === "number" ? (
                    readOnly ? (
                        <div style={{ display: "flex", alignItems: "baseline", minWidth: 0 }}>
                            {displayVal(cellObj.value, cellMax)}
                            {cellObj.value && cellObj.unit_id && (col.dropdown_options || []).length > 0 && (
                                <span className="tdf-unit">
                                    {units.find(u => String(u.id) === String(cellObj.unit_id))?.unit || cellObj.unit_id}
                                </span>
                            )}
                        </div>
                    ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <input
                            type="number"
                            value={cellObj.value ?? ""}
                            onChange={e => handleInputChange(rIndex, gIndex, cIndex, { value: e.target.value })}
                            placeholder="Enter number..."
                            className="tdf-input"
                        />
                        {(col.dropdown_options || []).length > 0 && (
                            readOnly ? null : (
                                <select
                                    value={cellObj.unit_id ?? ""}
                                    onChange={e => handleInputChange(rIndex, gIndex, cIndex, { unit_id: e.target.value || null })}
                                    className="tdf-input"
                                >
                                    <option value="">Unit (optional)</option>
                                    {(col.dropdown_options || []).map((opt, i) => {
                                        const matchedUnit = units.find((u) => u.id === opt);
                                        return (
                                            <option key={i} value={opt}>
                                                {matchedUnit?.unit || opt}
                                            </option>
                                        );
                                    })}
                                </select>
                            )
                        )}
                    </div>
                    )
                ) : (
                    readOnly ? (
                        displayVal(cellObj.value, cellMax)
                    ) : (
                        <HoverExpandInput
                            value={cellObj.value ?? ""}
                            onChange={(v) => handleInputChange(rIndex, gIndex, cIndex, { value: v, unit_id: null })}
                            placeholder={`Enter ${col.data_type || "text"}...`}
                        />
                    )
                )}
            </td>
        );
    };

    return (
        <div className="tdf-wrap">
            {/* Group management toolbar */}
            {!readOnly && (
                <div className="tdf-toolbar">
                    {hasGroups ? (
                        <GroupsDropdown
                            groups={columnGroups}
                            onRename={renameGroup}
                            onRemove={(gIdx) => {
                                if (safeGroupIdx >= columnGroups.length - 1) setActiveGroupIdx(Math.max(0, columnGroups.length - 2));
                                removeGroup(gIdx);
                            }}
                            onAdd={() => { addGroup(); setActiveGroupIdx(columnGroups.length); }}
                        />
                    ) : (
                        <button onClick={addGroup} className="tdf-toolbar-btn">+ Add Column Groups</button>
                    )}
                </div>
            )}
            {/* Group tab selector */}
            {hasGroups && (
                <div className="tdf-group-tabs">
                    {columnGroups.map((gName, gIdx) => (
                        <button
                            key={gIdx}
                            type="button"
                            className={`tdf-group-tab${gIdx === safeGroupIdx ? " tdf-group-tab-active" : ""}`}
                            onClick={() => setActiveGroupIdx(gIdx)}
                        >
                            <span className="tdf-group-tab-dot" style={{ background: GROUP_COLORS[gIdx % GROUP_COLORS.length] }} />
                            {gName || `Group ${gIdx + 1}`}
                        </button>
                    ))}
                </div>
            )}
            {/* per-row column variants can't share one column grid — keep auto layout there */}
            <table className={fixedRowsDynamicColumns ? "tdf-table" : "tdf-table tdf-table-fixed"}>
                <thead>
                    {hasGroups ? (
                        <tr>
                            {fixedRowsFixedColumns && hasAnyCategoryLabel && <th className="tdf-th" style={{ width: 240 }}>Category</th>}
                            {fixedRowsDynamicColumns && hasAnyCategoryLabel && <th className="tdf-th" style={{ minWidth: 120 }}>Category</th>}
                            {!fixedRowsDynamicColumns && (colsPerGroup?.[safeGroupIdx] || []).map((col, cIdx) => (
                                <th key={cIdx} className="tdf-th" style={{ minWidth: 160, width: 180 }}>{labelVal(col.option)}</th>
                            ))}
                            {dynamicRowsFixedColumns && !readOnly && <th className="tdf-th" style={{ width: 40 }} />}
                        </tr>
                    ) : !fixedRowsDynamicColumns ? (
                        <tr>
                            {fixedRowsFixedColumns && hasAnyCategoryLabel && <th className="tdf-th" style={{ width: 240 }}>Category</th>}
                            {getColumnsForRow(0).map((col, cIndex) => (
                                <th key={cIndex} className="tdf-th" style={{ minWidth: 160, width: 180 }}>{labelVal(col.option)}</th>
                            ))}
                            {dynamicRowsFixedColumns && !readOnly && (
                                <th className="tdf-th" style={{ width: 40 }} />
                            )}
                        </tr>
                    ) : null}
                </thead>
                <tbody>
                    {gridData.map((rawRow, rIndex) => {
                        const rowVals = rawRow ?? []; // stored answers can contain null rows
                        const rowDef = dynamicRowsFixedColumns ? null : rowOptions[rIndex];
                        const cols = getColumnsForRow(rIndex);
                        return (
                            <React.Fragment key={rIndex}>
                                {fixedRowsDynamicColumns && !hasGroups && (
                                    <tr>
                                        <td colSpan={cols.length} className="tdf-td-row-label">
                                            {labelVal(rowDef?.option || `Row ${rIndex + 1}`, 900)}
                                        </td>
                                    </tr>
                                )}
                                <tr className="tdf-tr">
                                    {fixedRowsDynamicColumns && hasGroups && hasAnyCategoryLabel && (
                                        <td className="tdf-td-cat" style={{ minWidth: 120 }}>
                                            {labelVal(rowDef?.option || `Row ${rIndex + 1}`, 560)}
                                        </td>
                                    )}
                                    {fixedRowsFixedColumns && hasAnyCategoryLabel && (
                                        <td className="tdf-td-cat">{labelVal(rowDef?.option)}</td>
                                    )}
                                    {hasGroups ? (
                                        (fixedRowsDynamicColumns ? cols : (colsPerGroup?.[safeGroupIdx] || [])).map((col, cIdx) => {
                                            const cellObj = (rowVals[safeGroupIdx] || [])[cIdx];
                                            return renderCell(col, cellObj, rIndex, safeGroupIdx, cIdx);
                                        })
                                    ) : (
                                        cols.map((col, cIndex) => {
                                            const cellObj = rowVals[cIndex] ?? {};
                                            return renderCell(col, cellObj, rIndex, cIndex, 0);
                                        })
                                    )}
                                    {dynamicRowsFixedColumns && !readOnly && (
                                        <td className="tdf-td-rm">
                                            <button
                                                onClick={() => removeRow(rIndex)}
                                                disabled={gridData.length <= 1}
                                                className="tdf-rm-btn"
                                            >
                                                &times;
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>

            {dynamicRowsFixedColumns && !readOnly && (
                <div className="tdf-add-row-wrap">
                    <button onClick={addRow} className="tdf-toolbar-btn">+ Add Row</button>
                </div>
            )}
        </div>
    );
};

export default TabularDynamicForm;
